/**
 * Defines the supported user languages.
 */
export enum UserLanguages {
    EN = "EN",
    DE = "DE",
    ES = "ES",
    FR = "FR",
}


/**
 * Defines the supported model types that can be handled by the system.
 */
export enum TransactionTypes {
    CASH = "CASH",
    SYNTHETIC = "SYNTHETIC",
}

export enum PaymentFrequencies {
    MONTHLY = "MONTHLY",
    QUARTERLY = "QUARTERLY",
    YEARLY = "YEARLY",
}

export enum EvaluationStatusEnum {
    PENDING = "PENDING",
    RUNNING = "RUNNING",
    COMPLETED = "COMPLETED",
    ERROR = "ERROR"
}

export enum LossDefinitionEnum {
    AMORTISATION_ONLY = "AMORTISATION ONLY",
    AMORTISATION_AND_INTEREST = "AMORTISATION AND INTEREST"
}

export enum DefaultDefinitionEnum{
    DEFERRABLE = "DEFERRABLE",
    NON_DEFERRABLE = "NON-DEFERRABLE"
}

export enum FormulaTypeEnum {
    STANDARD = "STANDARD",
}


export enum TriggerTypeEnum {
    REVERSIBLE = "REVERSIBLE",
    IRREVERSIBLE = "IRREVERSIBLE"
}


export enum TriggerConditionEnum {
    RUN_IF = "RUN_IF",
    RUN_IF_NOT = "RUN_IF_NOT"
}


export enum VariableTypeEnum {
    PORTFOLIO = "PORTFOLIO",
    TIME_SERIES = "TIME_SERIES",
    FORMULA = "FORMULA",
    TRIGGER = "TRIGGER",
    WATERFALL = "WATERFALL"
}


export enum PortfolioTypeEnum {
    MAIN = "MAIN",
    REPLENISHMENT = "REPLENISHMENT",
    RAMP_UP = "RAMP_UP"
}


export enum DisplayFormatTypeEnum {
    AMOUNT = "AMOUNT",
    PERCENTAGE = "PERCENTAGE",
    DECIMAL_RATE = "DECIMAL_RATE",
    INTEGER_NUMBER = "INTEGER_NUMBER",
    TIME = "TIME"
}


export enum VariableAggregationTypeEnum {
    CUMULATIVE = "CUMULATIVE",
    NON_CUMULATIVE = "NON_CUMULATIVE"
}

export enum WaterfallItemTypeEnum {
    CASH_TRANCHE = "CASH_TRANCHE",
    CASH_RESERVE = 'CASH_RESERVE',
    FEE = 'FEE',
    REPLENISHMENT = "REPLENISHMENT",
    SYNTHETIC_TRANCHE = "SYNTHETIC_TRANCHE",
    SYNTHETIC_EXCESS_SPREAD = "SYNTHETIC_EXCESS_SPREAD",
    PDL = "PDL"
}

export enum WaterfallProcessableTypeEnum {
    FORMULA = 'FORMULA',
    TRIGGER = 'TRIGGER',
    WATERFALL = 'WATERFALL'
}

export enum TimeSeriesTypeEnum {
    INTEREST_RATE = 'INTEREST_RATE',
    FX_RATE = 'FX_RATE',
    AMORTIZATION_RATE = 'AMORTIZATION_RATE',
    OTHER = 'OTHER'
}


export enum AmortizationTypeEnum {
    BULLET = 'BULLET',
    LINEAR = 'LINEAR',
    ANNUITY = 'ANNUITY',
    CUSTOM = 'CUSTOM'
}

export enum FormulaOperatorEnum{
    PLUS = 'PLUS',
    MINUS = 'MINUS',
    MULTIPLY = 'MULTIPLY',
    DIVIDE = 'DIVIDE',
    PLUS_BRACKET = 'PLUS_BRACKET',
    MINUS_BRACKET = 'MINUS_BRACKET',
    MULTIPLY_BRACKET = 'MULTIPLY_BRACKET',
    DIVIDE_BRACKET = 'DIVIDE_BRACKET',
    MAX = 'MAX',
    MIN = 'MIN'
}

export enum FormulaVariablePreProcessingTypeEnum {
    TEMPORAL_MAX = 'TEMPORAL_MAX',
    TEMPORAL_MIN = 'TEMPORAL_MIN',
    TEMPORAL_SUM = 'TEMPORAL_SUM',
    TEMPORAL_MEAN = 'TEMPORAL_MEAN',
    PREVIOUS_PERIOD = 'PREVIOUS_PERIOD'
}

export enum TriggerLogicalOperatorEnum {
    AND = "AND",
    OR_BRACKETS = "OR_BRACKETS",
}

export enum TriggerComparisonOperatorEnum {
    EQUAL = "EQUAL",
    LT = "LESS",
    GT = "GREATER",
    LE = "LESS_OR_EQUAL",
    GE = "GREATER_OR_EQUAL"
}

export enum DayCountConventionEnum {
    ACT_360 = 'ACT/360',
    ACT_365 = 'ACT/365',
    ACT_ACT_ISDA = 'ACT/ACT ISDA',
    ACT_ACT_AFB = 'ACT/ACT AFB',
    ACT_ACT_EURO = 'ACT/ACT EURO',
    THIRTY_360 = '30/360',
    THIRTY_365 = '30/365'
}

export enum RiskPercentileEnum {
    PERCENTILE_90 = 0.9,
    PERCENTILE_95 = 0.95,
    PERCENTILE_99 = 0.99,
}

export enum WaterfallItemConceptEnum{
    AMORTIZATION = "AMORTIZATION",
    INTEREST = "INTEREST",
    UNLIMITED_INTEREST = "UNLIMITED_INTEREST",
    CASH_TRAPPING = "CASH_TRAPPING",
    PAY = "PAY",
    PDL_ASSIGN = "PDL_ASSIGN",
    PDL_AMORTIZE = "PDL_AMORTIZE"
}

/**
 * Holds definition constants for the role asking a question via AI chat.
 * Mirrors backend ChatRole(StrEnum) values.
 */
export enum ChatRole {
    USER = "user",
    TOOL = "tool",
    ASSISTANT = "assistant",
    SYSTEM = "system",
}

/**
 * Defines the colors used in the system
 */
export enum ColorPalette {
    TRANSACTION = "rgb(200, 200, 255)",
}


/**
 * Holds the api routes available on the backend server
 */
export enum ApiEndPoints {
    TRANSACTION = '/api/transaction',
    PORTFOLIO = '/api/portfolio',
    PORTFOLIO_ROW = '/api/portfolio_row',
    VARIABLE = '/api/variable',
    TIME_SERIES = '/api/time_series',
    TIME_SERIES_ROW = '/api/time_series_row',
    WATERFALL_ITEM = '/api/waterfall_item',
    CASH_TRANCHE = '/api/cash_tranche',
    CASH_RESERVE = '/api/cash_reserve',
    FEE = '/api/fee',
    FORMULA = '/api/formula',
    FORMULA_ROW = '/api/formula_row',
    TRIGGER = '/api/trigger',
    TRIGGER_ROW = '/api/trigger_row',
    WATERFALL = '/api/waterfall',
    WATERFALL_AVAILABLE_FUNDS_ROW = '/api/waterfall_available_funds_row',
    WATERFALL_PAYMENT_PRIORITY_ROW = '/api/waterfall_payment_priority_row',
    EVALUATION = '/api/evaluation',
    WMT = '/api/wmt',
    WATERFALL_ITEM_CONCEPTS = '/api/waterfall_item_concepts',
    SYNTHETIC_TRANCHE = '/api/synthetic_tranche',
    SYNTHETIC_EXCESS_SPREAD = '/api/synthetic_excess_spread',
    SCENARIO_RESULTS = '/api/scenario_results',
    REPLENISHMENT = '/api/replenishment',
    TRANSACTION_SETTINGS = '/api/transaction_settings',
    PORTFOLIO_STATISTICS = '/api/portfolio_statistics',
    CHAT = '/api/chat',
}

/**
 * Holds the url paths of the different views on the frontend.
 */
export enum FrontendURLPaths {
    LANDING_PAGE = '/',
    DESIGN_CENTER = '/design-center',
}


/**
 * Holds the url paths of the images used in the frontend.
 */
export enum ImageURLs {
    CREATE_TRANSACTION = '/logo512.png',
}


/**
 * This enumerator list the types of feedback messages to be displayed on screen to provide feedback to the user.
 */
export enum FeedbackMessageTypes {
    ERROR = 'Error',
    WARNING = 'Warning',
    INFO = 'Info',
    SUCCESS = 'Success',
}


/**
 * This enumerator list the time spans in seconds for which feedback messages shall be displayed on screen to provide
 * feedback to the user. The display times can depend on the type of the feedback message as detailed here.
 */
export enum FeedbackMessageDisplayTimes {
    ERROR = 5,
    WARNING = 4,
    INFO = 10,
    SUCCESS = 3,
}


/**
 * This enumerator lists the http methods that are used for communicating with the backend api server.
 */
export enum HttpMethods {
    GET = 'GET',
    PUT = 'PUT',
    POST = 'POST',
    DELETE = 'DELETE',
}


/**
 * This enumerator lists all available id types that can be used for navigating to views on the frontend.
 */
export enum IDTypes {
    TRANSACTION = 'transaction_id',
    FILE = 'file_id',
}


export enum ValidationSchemes {
    REQUIRED = "Required",
    NUMBER = "Number",
    INTEGER = "Integer",
    DATE = "DATE",
    NON_NEGATIVE = "MIN_0",
    NON_POSITIVE = "MAX_0",
    MIN_1 = "MIN_1",
    MAX_LENGTH_6 = "MAXLENGTH_6",
    MIN_LENGTH_6 = "MINLENGTH_6",
}


export enum TextfieldInputTypes {
    DATE = "date",
    EMAIL = "email",
    PASSWORD = "password",
    TEXT = "text",
    NUMBER = "number",
}


/**
 * Defines the keys of the label-text-messages available in the system.
 */
export enum LabelTextMessageTypes {
    HOME,
    CONTACTS,
    LANGUAGE,
    ENGLISH,
    GERMAN,
    SPANISH,
    FRENCH,
    UNKNOWN_NETWORK_ERROR,
    MALFORMED_FETCH_REQUEST,
    LOGIN,
    LOGIN_HEADER,
    LOGIN_BUTTON_LABEL,
    LOGIN_SUCCESSFUL,
    CREATE,
    YES,
    NO,
    FILE_TYPE,
    FILE_SIZE,
    TIMESTAMP,
    CSV_SEPARATOR,
    CSV_DECIMAL_SEPARATOR,
    CSV_THOUSAND_SEPARATOR,
    CSV_DATE_FORMAT,
    CSV_MISSING_PARAMETER_PLACEHOLDERS,
    COLUMN_HEADERS_INCLUDED,
    NAME,
    DESCRIPTION,
    RISK_MODEL_NAME,
    ACTIONS,
    SELECT_ALL,
    UNSELECT_ALL,
    LOAD,
    DROP_ZONE_HINT,
    MORE_DETAILS,
    PD_MODEL,
    LGD_MODEL,
    CCF_MODEL,
    ANY_MODEL,
    ORIG_COLUMN_NAME,
    MAPPED_COLUMN_NAME,
    COLUMN_TYPE,
    MACHINE_LEARNER_COMPARISON,
    BEST_OBSERVED_GINI,
    DEGREES_OF_FREEDOM,
    GINI_COEFFICIENT,
    MODEL_PERFORMANCE_VS_COMPLEXITY,
    PROGRESS,
    DISTRIBUTION,
    ROC_CURVE,
    TRUE_POSITIVE_RATE,
    FALSE_POSITIVE_RATE,
    OCCURRENCE_DENSITY,
    CONDITIONAL_PD,
    OCCURRENCE_RATE,
    PROBABILITY_SLASH_DENSITY,
    PROBABILITY_SLASH_RATE,
    PD_PER_CATEGORY,
    RDS_VARIABLE,
    TARGET_VAR_BINARY,
    TARGET_VAR_MULTI_CATEGORICAL,
    TARGET_VAR_NON_NEGATIVE,
    TARGET_VAR_UNBOUNDED,
    INPUT_VAR_NUMERICAL,
    INPUT_VAR_CATEGORICAL,
    LP_CREATE_OR_LOAD_CAPTION,
    LP_CREATE_OR_LOAD_EXPLANATION,
    LP_CREATE_PD_MODEL,
    LP_CREATE_LGD_MODEL,
    LP_CREATE_CCF_MODEL,
    LP_LOAD_MODEL,
    LP_CREATE_PD_MODEL_EXPLANATION,
    LP_CREATE_LGD_MODEL_EXPLANATION,
    LP_CREATE_CCF_MODEL_EXPLANATION,
    LP_LOAD_MODEL_EXPLANATION,
    NEW_TRANSACTION_CREATION_TITLE,
    NEW_TRANSACTION_CREATION_NAME,
    NEW_TRANSACTION_CREATION_DESCRIPTION,
    NEW_TRANSACTION_CREATION_EFFECTIVE_DATE,
    NEW_TRANSACTION_CREATION_FIRST_PAYMENT,
    NEW_TRANSACTION_CREATION_MATURITY_DATE,
    NEW_TRANSACTION_CREATION_FREQUENCY,
    NEW_TRANSACTION_CREATION_TYPE,
    NEW_TRANSACTION_CREATION_FREQUENCY_MONTHLY,
    NEW_TRANSACTION_CREATION_FREQUENCY_QUARTERLY,
    NEW_TRANSACTION_CREATION_FREQUENCY_YEARLY,
    TRANSACTION_CREATE_OR_LOAD_CAPTION,
    OR,
    NEW_TRANSACTION_CREATION_OWNER,
    SEARCHBAR,
    ALL_FIELDS_REQUIRED,
    SUCCESSFUL_CREATION,
    WAL,
    FairValue,
    PD,
    LGD,
    EL,
    GENERAL_TAB_CAPTION,
    GENERAL_TAB_EXPLANATION,
    LIABILITIES_TAB_CAPTION,
    LIABILITIES_TAB_EXPLANATION,
    ASSETS_TAB_CAPTION,
    ASSETS_TAB_EXPLANATION,
    TIMESERIES_TAB_CAPTION,
    TIMESERIES_TAB_EXPLANATION,
    FORMULA_TAB_CAPTION,
    FORMULA_TAB_EXPLANATION,
    TRIGGERS_TAB_CAPTION,
    TRIGGERS_TAB_EXPLANATION,
    WATERFALL_TAB_CAPTION,
    WATERFALL_TAB_EXPLANATION,
    WMT_TAB_CAPTION,
    WMT_TAB_EXPLANATION,
    AI_CHAT_TAB_CAPTION,
    AI_CHAT_TAB_EXPLANATION,
    SETTINGS_TAB_CAPTION,
    SETTINGS_TAB_EXPLANATION,
    TRANSACTION_UPDATED,
    UPDATE_TRANSACTION_TITLE,
    NEW_SYNTHETIC_TRANCHE_CREATION_TITLE,
    UPDATE_SYNTHETIC_TRANCHE_TITLE,
    SUBMIT,
    CANCEL,
    UPDATE_SYNTHETIC_EXCESS_SPREAD_TITLE,
    NEW_SYNTHETIC_EXCESS_SPREAD_CREATION_TITLE,
    UPDATE_CASH_RESERVE_TITLE,
    NEW_CASH_RESERVE_CREATION_TITLE,
    UPDATE_CASH_TRANCHE_TITLE,
    NEW_CASH_TRANCHE_CREATION_TITLE,
    UPDATE_FEE_TITLE,
    NEW_FEE_CREATION_TITLE,
    INITIAL_NOMINAL,
    RESERVE_RATE,
    SPREAD_RECEIVING,
    SPREAD_PAYING,
    BASE_CURVE_ID,
    SPREAD,
    DEFAULT_DEFINITION,
    INVESTED,
    FIXED_AMOUNT,
    VARIABLE_RATE,
    REFERENCE_VARIABLE_ID,
    EXCESS_SPREAD,
    SYNTHETIC_TRANCHE_UPDATED,
    CASH_TRANCHE_UPDATED,
    CASH_RESERVE_UPDATED,
    SYNTHETIC_EXCESS_SPREAD_UPDATED,
    FEE_UPDATED,
    INVALID_SCENARIO_NUMBER,
    SCENARIO_NUMBER,
    PORTFOLIO_UPDATED,
    PORTFOLIO_ROW_UPDATED,
    PORTFOLIO_CREATED,
    PORTFOLIO_ROW_CREATED,
    TARGET_RETURN_ON_EQUITY,
    TARGET_AMORTIZATION_VARIABLE_ID,
    TIMESERIES_CREATED,
    TIMESERIES_ROW_CREATED,
    TIMESERIES_UPDATED,
    TIMESERIES_ROW_UPDATED,
    WATERFALL_ITEM_DELETED,
    TIMESERIES_DELETED,
    PORTFOLIO_ROW_DELETED,
    PORTFOLIO_DELETED,
    TRIGGER_UPDATED,
    TRIGGER_DELETED,
    FORMULA_UPDATED,
    FORMULA_DELETED,
    FORMULA_ROW_UPDATED,
    FORMULA_ROW_DELETED,
    WATERFALL_UPDATED,
    WATERFALL_DELETED,
    WATERFALL_PAYMENT_PRIORITY_ROW_DELETED,
    WATERFALL_AVAILABLE_FUNDS_ROW_DELETED,
    WATERFALL_CREATED,
    NUMBER_OF_SIMULATIONS,
    DISCOUNTING_CURVE_ID,
    LOSS_DEFINITION,
    DAY_COUNT_CONVENTION,
    RISK_PERCENTILE,
    NO_TRANSACTION_LOADED,
    TRANSACTION_SETTINGS_UPDATED,
    TRANSACTION_SETTINGS,
    TRIGGER_CREATED,
    FORMULA_CREATED,
    VARIABLE_DROP_REQUIRED_PLACEHOLDER,
    VARIABLE_DROP_OPTIONAL_PLACEHOLDER,
}

export enum HTMLFormattedTextTypes {
    LP_CREATE_OR_LOAD_DETAILS,
    FU_FILE_UPLOAD_DETAILS,
    RC_RDS_CONSTRUCTION_DETAILS,
    UA_UNIVARIATE_ANALYSIS_DETAILS,
    MA_MULTIVARIATE_ANALYSIS_DETAILS,
    MS_MODEL_SELECTION_DETAILS,
    GENERAL_TAB_DETAILS,
    LIABILITIES_TAB_DETAILS,
    ASSETS_TAB_DETAILS,
    TIMESERIES_TAB_DETAILS,
    FORMULA_TAB_DETAILS,
    TRIGGERS_TAB_DETAILS,
    WATERFALL_TAB_DETAILS,
    WMT_TAB_DETAILS,
    AI_CHAT_TAB_DETAILS,
    SETTINGS_TAB_DETAILS,
}

/**
 * Defines the supported react-bootstrap button variants for dropdowns and other components.
 */
export enum RBSVariantEnum {
    PRIMARY = "primary",
    SECONDARY = "secondary",
    SUCCESS = "success",
    DANGER = "danger",
    WARNING = "warning",
    INFO = "info",
    LIGHT = "light",
    DARK = "dark",
    LINK = "link"
}

export const DESIGN_CENTER_BOTTOM_OFFSET_PX = 30;
